import { makeStyles } from '@mui/styles'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo, useRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Box, Typography } from '@mui/material'
import JobSummarySettingsDialog from './JobSummarySettingsDialog'
import { IconThemeProvider } from 'custom-components/context/IconThemesContext'
import LoadingSpinner from 'custom-components/LoadingSpinner'
import TileWrapper from '../../components/TileWrapper'
import { JobSummaryComponent } from './JobSummaryComponent'

import { tileKeys } from '../../hooks/useTileQuery'
import tileApi from 'apis/disApi/tileApi'
import useDashboardQuery from '../../hooks/useDashboardQuery'
import { useTileDashboard } from '../../dashboard/Dashboard'
import { isJson, logErrorMessage } from 'utils/functions/helpers'
import { useSearchParams } from 'utils/hooks/useSearchParams'
import { useModalDashboardContext } from '../modal-dashboard/helper/modalDashboardContext'

const useStyles = makeStyles(() => ({
	content: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%',
		width: '100%',
	},
}))

const JobSummaryTile = ({ tile }) => {
	const { environment } = useSelector((state) => state)
	const [t] = useTranslation('common')
	const iconTheme = environment.theme.icons
	const classes = useStyles(iconTheme)

	const queryClient = useQueryClient()
	const { id: dashboardKey } = useParams()
	const { menuKey: menuKeyInUrl } = useSearchParams()

	const tileRef = useRef(null)
	const containerBoxRef = useRef(null)

	const { passedIdValue: parentPassedIdValue, modalDashboardSetting: parentModalDashboardSetting } =
		useModalDashboardContext()

	const currentDashboardKey = useMemo(
		() => parentModalDashboardSetting?.dashboardKey ?? dashboardKey,
		[parentModalDashboardSetting, dashboardKey]
	)

	const [settingsOpen, setSettingsOpen] = useState(false)

	const [tileWrapperHeight, setTileWrapperHeight] = useState(null)

	const settings = useMemo(() => {
		if (tile?.settings && isJson(tile?.settings)) {
			return JSON.parse(tile?.settings ?? '{}')
		}
		return {}
	}, [tile?.settings])

	const { updateMutation: updateDashboard, dashboardKeyList } = useDashboardQuery({
		dashboardKey: currentDashboardKey,
	})

	const updateTileMutation = useMutation(tileApi.update, {
		onSuccess: () => queryClient.invalidateQueries(tileKeys.allWithKey(currentDashboardKey)),
	})

	const handleOpenDialog = () => {
		setSettingsOpen(true)
	}

	const handleCloseDialog = () => {
		setSettingsOpen(false)
	}

	const handleSubmitSetting = async (data) => {
		try {
			const { primaryKeyList, ...restData } = data

			const isHavePrimaryKey = Boolean(primaryKeyList)
			if (isHavePrimaryKey) {
				await updateDashboard.mutateAsync({
					key: currentDashboardKey,
					data: {
						variables: JSON.stringify(primaryKeyList),
					},
					token: environment.apiToken,
				})
			}

			handleCloseDialog()
			const editedSettings = JSON.stringify({
				...settings,
				...restData,
			})

			await updateTileMutation.mutateAsync({
				dashboardKey: currentDashboardKey,
				tileKey: tile.key,
				data: { settings: editedSettings },
				token: environment.apiToken,
			})
		} catch (error) {
			logErrorMessage(error)
		}
	}

	const handleResizeTileWidth = async (width) => {
		try {
			const editedSettings = JSON.stringify({
				...settings,
				tileWidth: width,
			})

			await updateTileMutation.mutateAsync({
				dashboardKey: currentDashboardKey,
				tileKey: tile.key,
				data: { settings: editedSettings },
				token: environment.apiToken,
			})
		} catch (error) {
			logErrorMessage(error)
		}
	}

	const isLoading = updateTileMutation.isLoading

	useEffect(() => {
		const containerBox = containerBoxRef.current
		if (containerBox) {
			// Calculate the height of the tile wrapper minus the header height
			const height = containerBox.offsetHeight
			if (tileWrapperHeight === height) return
			setTileWrapperHeight(height)
		}
	}, [tile?.position, containerBoxRef])

	return (
		<IconThemeProvider values={iconTheme}>
			<TileWrapper title={tile?.i} onSettingClick={handleOpenDialog} ref={tileRef}>
				{isLoading && <LoadingSpinner />}

				<JobSummarySettingsDialog
					tile={tile}
					settings={settings}
					isSubmitting={isLoading}
					open={settingsOpen}
					onClose={handleCloseDialog}
					onSubmit={handleSubmitSetting}
					dashboardKeyList={dashboardKeyList}
					tileElementWidth={tileRef?.current?.clientWidth}
					defaultTileWidth={settings?.tileWidth}
					onResizeTileWidth={handleResizeTileWidth}
					menuKeyInUrl={menuKeyInUrl}
				/>

				<Box
					ref={containerBoxRef}
					sx={{
						backgroundColor: 'white',
						height: '100%',
						width: '100%',
					}}
				>
					<JobSummaryComponent
						tile={tile}
						settings={settings}
						dashboardKeyList={dashboardKeyList}
						tileWrapperHeight={tileWrapperHeight}
						passedIdValue={parentPassedIdValue}
						modalDashboardSetting={parentModalDashboardSetting}
					/>
				</Box>
			</TileWrapper>
		</IconThemeProvider>
	)
}

export default JobSummaryTile
